import { useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "./ui/responsive-popover";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { ACQUISITION_CHANNELS, parseAcquisitionChannels } from "../lib/pipeline";

const labelFor = (id) => ACQUISITION_CHANNELS.find((c) => c.id === id)?.label || id;

// Multi-select for the acquisition channel(s): a prospect can have more than
// one (e.g. seen on social media, then filled the web form). Stored as a single
// comma-separated string on `acquisition_channel`; this component owns the
// join/split. Also accepts free text, since the closed list can't anticipate
// every channel a commercial might need to log.
export default function AcquisitionChannelField({ value, onChange, disabled }) {
  const selected = parseAcquisitionChannels(value);
  const [customInput, setCustomInput] = useState("");

  // Anything selected that isn't one of the predefined ids is a custom value
  // someone typed in, shown as its own removable row below the fixed list.
  const customSelected = selected.filter((id) => !ACQUISITION_CHANNELS.some((c) => c.id === id));

  const toggle = (id) => {
    const next = selected.includes(id)
      ? selected.filter((c) => c !== id)
      : [...selected, id];
    onChange(next.join(","));
  };

  const addCustom = () => {
    // Commas are the storage separator: stripped so a typed comma can't
    // silently split into two unintended tags.
    const v = customInput.trim().replace(/,/g, "");
    if (!v || selected.includes(v)) {
      setCustomInput("");
      return;
    }
    onChange([...selected, v].join(","));
    setCustomInput("");
  };

  const triggerLabel = selected.length === 0 ? "Sélectionner..." : selected.map(labelFor).join(", ");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid="select-acquisition-channel"
          className="w-full justify-between font-normal"
        >
          <span className="truncate text-left text-slate-700">{triggerLabel}</span>
          <ChevronDown size={14} className="ml-2 text-slate-400 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="space-y-0.5 max-h-56 overflow-y-auto scrollbar-thin">
          {ACQUISITION_CHANNELS.map((c) => (
            <label
              key={c.id}
              data-testid={`toggle-acquisition-channel-${c.id}`}
              className="flex items-center gap-2.5 py-1.5 px-1 rounded hover:bg-slate-50 cursor-pointer"
            >
              <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
              <span className="text-sm text-slate-700">{c.label}</span>
            </label>
          ))}
          {customSelected.length > 0 && (
            <>
              <div className="h-px bg-slate-100 my-1" />
              {customSelected.map((v) => (
                <div key={v} className="flex items-center gap-2.5 py-1.5 px-1 rounded hover:bg-slate-50">
                  <Checkbox checked onCheckedChange={() => toggle(v)} />
                  <span className="text-sm text-slate-700 truncate flex-1">{v}</span>
                  <button
                    type="button"
                    onClick={() => toggle(v)}
                    aria-label={`Retirer ${v}`}
                    className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5 pt-2 mt-2 border-t border-slate-100">
          <Input
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addCustom();
              }
            }}
            placeholder="Autre canal…"
            data-testid="input-acquisition-channel-custom"
            className="h-8 text-sm"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={addCustom}
            disabled={!customInput.trim()}
            data-testid="btn-add-acquisition-channel-custom"
          >
            Ajouter
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
